import type { RefObject } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export const useSettledInView = <T extends HTMLElement = HTMLDivElement>({
  settleMs,
  rootMargin = '200px',
  threshold = 0.1,
}: {
  settleMs: number;
  rootMargin?: string;
  threshold?: number;
}): { elementRef: RefObject<T>; isInView: boolean; hasSettledInView: boolean } => {
  const elementRef = useRef<T>(null);
  const [isInView, setIsInView] = useState(false);
  const [hasSettledInView, setHasSettledInView] = useState(false);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(Boolean(entry?.isIntersecting)),
      { root: null, rootMargin, threshold }
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [rootMargin, threshold]);

  useEffect(() => {
    if (hasSettledInView || !isInView) return undefined;

    const timeout = setTimeout(() => setHasSettledInView(true), settleMs);

    return () => clearTimeout(timeout);
  }, [isInView, hasSettledInView, settleMs]);

  return { elementRef, isInView, hasSettledInView };
};
